import { Action } from 'redux';
import { DataAction } from '../@types';
import { ErrorOrFail } from '../async/actionCreators/types';
import { call, put } from 'redux-saga/effects';
import { error, serviceUnavailableError, unauthorizedError } from '../actions';
import { ErrorOrFailStatusCode, HttpResponse, HttpStatusCode, SuccessStatusCode } from '@sonar-software/sonar-http';

const fiveMinutes = 5 * 60 * 1000;

function isOfAnyStatus(statusCode: HttpStatusCode, expectedStatuses: HttpStatusCode[]): boolean {
	return expectedStatuses.filter((expectedStatus) => statusCode === expectedStatus).length > 0;
}

type HttpCache<TData> = Record<string, { response: HttpResponse<TData>, time: number }>;

export const DefaultErrorCodes = [
	ErrorOrFailStatusCode.BadGateway,
	ErrorOrFailStatusCode.Forbidden,
	ErrorOrFailStatusCode.NotAcceptable,
	ErrorOrFailStatusCode.NotFound,
	ErrorOrFailStatusCode.RequestTimeout,
	ErrorOrFailStatusCode.ServerError,
	ErrorOrFailStatusCode.UnprocessableEntity
];

export const DefaultFailCodes = [
	ErrorOrFailStatusCode.BadRequest,
	ErrorOrFailStatusCode.Conflict,
	ErrorOrFailStatusCode.Locked,
	ErrorOrFailStatusCode.Unauthorized,
	ErrorOrFailStatusCode.ServiceUnavailable
];

export interface StatusCodeHandlerOptions {
	actionCreators: ((errorOrFail: ErrorOrFail) => Action)[];
	statuses: ErrorOrFailStatusCode[];
}

export interface HttpCacheOptions {
	duration: number;
}

export interface HttpSagaOptions<TRequest, TData> {
	allowUnauthorized?: boolean;
	cache?: boolean | HttpCacheOptions;
	errorCodeActionCreators?: ((errorOrFail: ErrorOrFail) => Action)[];
	failCodeActionCreators?: ((errorOrFail: ErrorOrFail) => Action)[];
	preRequestActionCreators?: ((request: TRequest) => Action)[];
	requestHandler: (
		request?: TRequest
	) => Promise<HttpResponse<TData>> | Generator<unknown, unknown, unknown>;
	statusCodeHandlers?: StatusCodeHandlerOptions[];
	successCombinationActionCreators?: ((requestData: TRequest, data: TData) => Action)[];
	successDataActionCreators?: ((data: TData) => Action)[];
	successDataHandlers?: ((data: TData) => void)[];
	successActionCreators?: (() => Action)[];
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function createHttpSaga<
	TActionType extends string,
	TActionData,
	TData
>(options: HttpSagaOptions<TActionData, TData>) {
	const {
		allowUnauthorized,
		errorCodeActionCreators = [],
		failCodeActionCreators = [],
		preRequestActionCreators = [],
		requestHandler,
		statusCodeHandlers = [],
		successDataActionCreators = [],
		successCombinationActionCreators = [],
		successDataHandlers = [],
		successActionCreators = []
	} = options;

	if (errorCodeActionCreators.length) {
		statusCodeHandlers.push({
			actionCreators: errorCodeActionCreators,
			statuses: DefaultErrorCodes
		});
	}

	if (failCodeActionCreators.length) {
		statusCodeHandlers.push({
			actionCreators: failCodeActionCreators,
			statuses: DefaultFailCodes
		});
	}

	const cache: HttpCache<TData> | undefined = options.cache ? {} : undefined;

	const cacheOptions: HttpCacheOptions = typeof options.cache === 'object' ?
		options.cache : { duration: fiveMinutes };

	function tryGetFromCache(requestData: TActionData): HttpResponse<TData> | undefined {
		const key = JSON.stringify(requestData) ?? 'undefined';

		if (!cache || !cache[ key ]) {
			return undefined;
		}

		const entry = cache[ key ];

		if (Date.now() - cacheOptions.duration > entry.time) {
			delete cache[ key ];

			return undefined;
		}

		return entry.response;
	}

	function setCache(requestData: TActionData, response: HttpResponse<TData>): void {
		if (!cache) {
			return;
		}

		const key = JSON.stringify(requestData) ?? 'undefined';

		cache[ key ] = {
			response,
			time: Date.now()
		};
	}

	// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
	return function* getApi(action: Action<TActionType> | DataAction<TActionType, TActionData>) {
		try {
			const requestData = (action as DataAction<TActionType, TActionData>).data;

			for (let i = 0; i < preRequestActionCreators.length; i += 1) {
				yield put(preRequestActionCreators[ i ](requestData));
			}

			let result: HttpResponse<TData>;

			const cacheResult = tryGetFromCache(requestData);

			if (cacheResult) {
				result = cacheResult;
			} else if (requestData) {
				result = yield call(requestHandler, requestData);
			} else {
				result = yield call(requestHandler);
			}

			const { status } = result;

			if (
				status === SuccessStatusCode.Ok ||
				status === SuccessStatusCode.Accepted ||
				status === SuccessStatusCode.NoContent
			) {
				if (!cacheResult) {
					setCache(requestData, result);
				}

				for (let i = 0; i < successDataHandlers.length; i += 1) {
					successDataHandlers[ i ](<TData>result.data);
				}

				for (let i = 0; i < successActionCreators.length; i += 1) {
					yield put(successActionCreators[ i ]());
				}

				for (let i = 0; i < successDataActionCreators.length; i += 1) {
					yield put(successDataActionCreators[ i ](<TData>result.data));
				}

				if (requestData) {
					for (let i = 0; i < successCombinationActionCreators.length; i += 1) {
						yield put(successCombinationActionCreators[ i ](requestData, result.data as TData));
					}
				}
			}

			if (status === ErrorOrFailStatusCode.Unauthorized && !allowUnauthorized) {
				yield put(unauthorizedError(action));
			}

			if (status === ErrorOrFailStatusCode.ServiceUnavailable) {
				yield put(serviceUnavailableError(action));
			}

			for (let i = 0; i < statusCodeHandlers.length; i += 1) {
				const handler = statusCodeHandlers[ i ];

				if (isOfAnyStatus(status, handler.statuses)) {
					for (let x = 0; x < handler.actionCreators.length; x += 1) {
						yield put(handler.actionCreators[ x ]({ statusCode: <ErrorOrFailStatusCode>status }));
					}
				}
			}
		} catch (e) {
			yield put(error((e as Error).message));
		}
	};
}
